import React from "react";
import styled from "@emotion/styled";
import { Avatar, Box, Collapse, Tooltip, Typography } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Modal } from "@mui/material";
import { NavModalLogout } from "../NavModal/NavModalLogout";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import { selectAuthState } from "../../store/Auth/authStore";
import { selectEventState } from "../../store/Event/eventStore";
import { selectGames } from "../../store/Games/gamesStore";
import { getGames } from "../../store/Games/thunks";
import {
  Dices,
  Plug,
  Users,
  ChartLine,
  BookOpen,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  LogOut,
  Route,
} from "lucide-react";
import { getMonthDates } from "../../utils/getCurrenMonth";
import { format } from "date-fns";

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// Light sidebar palette per Gamixter Design System (components/navigation/Sidebar.jsx):
// white surface, active item = accent tint bg + accent text.
const sb = {
  bg: "var(--g-surface)",
  divider: "var(--g-border)",
  itemText: "var(--g-text-2)",
  itemMuted: "var(--g-text-3)",
  hover: "var(--g-surface-2)",
  active: "var(--g-accent-tint)",
  activeText: "var(--g-accent)",
  accent: "var(--g-accent)",
  accentSoft: "var(--g-accent-tint)",
  highlighted: "var(--g-accent-tint-2)",
};

const SidebarRoot = styled.aside<{ collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ collapsed }) => (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH)}px;
  background: ${sb.bg};
  border-right: 1px solid var(--g-border);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.22s ease;
  overflow: hidden;
`;

const SideDivider = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: ${sb.divider};
  margin: 0 12px;
`;

const LogoArea = styled.div<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  height: 72px;
  flex-shrink: 0;
  padding: 0 10px;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
`;

const SidebarToggle = styled.button<{ collapsed: boolean }>`
  position: fixed;
  left: ${({ collapsed }) => (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH)}px;
  top: 36px;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--g-border-strong);
  background: var(--g-surface);
  box-shadow: var(--g-shadow);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1101;
  transition: left 0.22s ease, background 0.15s, color 0.15s;
  color: var(--g-text-2);
  padding: 0;
  &:hover {
    background: var(--g-accent);
    border-color: var(--g-accent);
    color: #fff;
  }
  svg { width: 15px; height: 15px; }
`;

const NavList = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;
`;

const NavItem = styled.div<{ active?: boolean; collapsed?: boolean; highlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 16px")};
  margin: 2px ${({ collapsed }) => (collapsed ? "4px" : "8px")};
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  background: ${({ active, highlighted }) =>
    highlighted ? sb.highlighted : active ? sb.active : "transparent"};
  color: ${({ active, highlighted }) =>
    highlighted ? sb.accent : active ? sb.activeText : sb.itemText};
  font-weight: ${({ active }) => (active ? 600 : 500)};
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  &:hover {
    background: ${({ active }) => (active ? sb.active : sb.hover)};
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${({ active, highlighted }) =>
      highlighted ? sb.accent : active ? sb.accent : sb.itemMuted};
  }
`;

const SubItem = styled.div<{ active?: boolean; highlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 16px 0 40px;
  margin: 1px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  background: ${({ active, highlighted }) =>
    highlighted ? sb.highlighted : active ? sb.active : "transparent"};
  color: ${({ active, highlighted }) =>
    highlighted ? sb.accent : active ? sb.activeText : sb.itemText};
  &:hover {
    background: ${({ active }) => (active ? sb.active : sb.hover)};
  }
`;

const SubItemDot = styled.div<{ active?: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ active }) => (active ? sb.accent : sb.itemMuted)};
`;

const ItemLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  flex: 1;
`;

// Journeys live in VOKO, a separate product: the link wears VOKO's colours, not ours.
const VOKO_URL = "https://www.voko.is/voko";
const voko = {
  gradient: "linear-gradient(135deg, #B8E62E 0%, #D8F04A 55%, #FFF08A 100%)",
  ink: "#1C1309",
  deep: "#4D7C0F",
};

const JourneysLink = styled.a<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  gap: 12px;
  height: 40px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 16px")};
  margin: 6px ${({ collapsed }) => (collapsed ? "4px" : "8px")} 2px;
  border-radius: 10px;
  background: ${voko.gradient};
  color: ${voko.ink};
  text-decoration: none;
  transition: filter 0.15s, box-shadow 0.15s;
  & svg { width: 20px; height: 20px; flex-shrink: 0; }
  &:hover { filter: brightness(1.04); box-shadow: 0 2px 8px rgba(77, 124, 15, 0.25); }
`;

const NewBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 6px;
  background: ${voko.ink};
  color: #D8F04A;
`;

const SubLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BottomArea = styled.div`
  padding: 12px 8px;
  flex-shrink: 0;
  margin-top: auto;
  border-top: 1px solid ${sb.divider};
`;

const MauBadge = styled.div<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  gap: 6px;
  padding: ${({ collapsed }) => (collapsed ? "2px 0" : "2px 8px")};
  margin: 0 ${({ collapsed }) => (collapsed ? "4px" : "8px")} 8px;
  border-radius: 999px;
  background: ${sb.accentSoft};
  color: ${sb.accent};
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${sb.highlighted}; }
`;

const UserRow = styled.div<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background: ${sb.hover};
  }
`;

interface Props {
  collapsed: boolean;
  onToggle: () => void;
  onRestartGuide?: () => void;
  highlightPath?: string;
  expandSection?: "games" | "integrations" | "players" | null;
}

export const Sidebar = ({ collapsed, onToggle, onRestartGuide, highlightPath, expandSection }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAppSelector(selectAuthState);
  const { monthlyActiveUsersCount } = useAppSelector(selectEventState);
  const { games, loading, loadingError } = useAppSelector(selectGames);
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [gamesOpen, setGamesOpen] = React.useState(true);
  const integrationPaths = ["/integrations", "/integration-events", "/webhook", "/trigger", "/bonus"];
  const [integrationsOpen, setIntegrationsOpen] = React.useState(
    integrationPaths.some(p => location.pathname.startsWith(p))
  );
  const playerPaths = ["/users", "/cohortes", "/currencies"];
  const [playersOpen, setPlayersOpen] = React.useState(
    playerPaths.some(p => location.pathname.startsWith(p))
  );
  const [startDate, currentDate] = getMonthDates();

  React.useEffect(() => {
    if (games.length === 0 && !loading && !loadingError) {
      dispatch(getGames());
    }
  }, []);

  React.useEffect(() => {
    if (expandSection == null) return;
    setGamesOpen(expandSection === "games");
    setIntegrationsOpen(expandSection === "integrations");
    setPlayersOpen(expandSection === "players");
  }, [expandSection]);

  const visibleGames = games.filter((g) => g.enabled);

  const getGamePath = (game: { systemName: string; version?: number; hidden?: boolean }) => {
    // Banners are normal listed games but keep their dedicated library editor.
    if (game.systemName === "bannerV2") return "/banners";
    if (game.systemName === "bannerV3") return "/banners-v3";
    if ((game.version ?? 0) >= 5) return `/v5/game/${game.systemName}`;
    if (game.hidden) return `/v3/game/${game.systemName}`;
    return `/game/${game.systemName}`;
  };

  const isGamesActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/v3/game") ||
    location.pathname.startsWith("/v5/game") ||
    location.pathname.startsWith("/banners");

  const navItems = [
    {
      key: "Analytics", label: "Analytics", path: "/events", icon: <ChartLine />,
      onClick: () => navigate(`/events?fromDate=${format(startDate, "yyyy-MM-dd'T'00:00:00")}&toDate=${format(currentDate, "yyyy-MM-dd'T'23:59:59")}`),
    },
    { key: "Docs", label: "Documentation", path: "/docs", icon: <BookOpen />, onClick: () => navigate("/docs") },
  ];

  const tip = (title: string, node: React.ReactElement) =>
    collapsed
      ? <Tooltip title={title} placement="right" key={title}>{node}</Tooltip>
      : node;

  return (
    <>
      <SidebarToggle collapsed={collapsed} onClick={onToggle}>
        {collapsed ? <ChevronRight /> : <ChevronLeft />}
      </SidebarToggle>
      <SidebarRoot collapsed={collapsed}>
        <LogoArea collapsed={collapsed}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: "9px",
              background: "var(--g-gradient-avatar)", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 800, lineHeight: 1 }}>G</Typography>
            </Box>
            {!collapsed && (
              <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.6px", color: "var(--g-text)", whiteSpace: "nowrap" }}>
                Gamixter
              </Typography>
            )}
          </Link>
        </LogoArea>

        <SideDivider />

        <NavList>
          {tip(t("Header.games"),
            <NavItem
              data-onboarding="games-section"
              active={isGamesActive}
              collapsed={collapsed}
              highlighted={highlightPath === "games-section"}
              onClick={() => { if (!collapsed) setGamesOpen((p) => !p); else navigate("/"); }}
            >
              <Dices />
              {!collapsed && (
                <>
                  <ItemLabel>{t("Header.games")}</ItemLabel>
                  {gamesOpen
                    ? <ChevronUp style={{ width: 16, height: 16, opacity: 0.5 }} />
                    : <ChevronDown style={{ width: 16, height: 16, opacity: 0.5 }} />
                  }
                </>
              )}
            </NavItem>
          )}

          {!collapsed && (
            <Collapse in={gamesOpen} timeout="auto">
              {visibleGames.map((game) => {
                const path = getGamePath(game);
                const active = location.pathname.startsWith(path);
                return (
                  <SubItem key={game.systemName} active={active} onClick={() => navigate(path)}>
                    <SubItemDot active={active} />
                    <SubLabel>{game.name}</SubLabel>
                    {(game.version ?? 0) >= 5 && (
                      <Box sx={{
                        bgcolor: "var(--g-accent-tint-2)", color: "var(--g-accent)",
                        fontSize: 11, fontWeight: 700, lineHeight: 1,
                        padding: "2px 8px", borderRadius: "999px",
                        textTransform: "uppercase", letterSpacing: 1.5, flexShrink: 0,
                      }}>{game.version === 6 ? "v3" : "v2"}</Box>
                    )}
                  </SubItem>
                );
              })}
              {visibleGames.length === 0 && (
                <SubItem>
                  <SubItemDot />
                  <SubLabel style={{ color: "var(--g-text-3)" }}>
                    {t("Header.games")}...
                  </SubLabel>
                </SubItem>
              )}
            </Collapse>
          )}

          {tip("Integrations",
            <NavItem
              data-onboarding="integrations-section"
              active={integrationPaths.some(p => location.pathname.startsWith(p))}
              collapsed={collapsed}
              highlighted={!!highlightPath && integrationPaths.includes(highlightPath)}
              onClick={() => { if (!collapsed) setIntegrationsOpen((p) => !p); else navigate("/integrations"); }}
            >
              <Plug />
              {!collapsed && (
                <>
                  <ItemLabel>Integrations</ItemLabel>
                  {integrationsOpen
                    ? <ChevronUp style={{ width: 16, height: 16, opacity: 0.5 }} />
                    : <ChevronDown style={{ width: 16, height: 16, opacity: 0.5 }} />
                  }
                </>
              )}
            </NavItem>
          )}

          {!collapsed && (
            <Collapse in={integrationsOpen} timeout="auto">
              {[
                { label: "Setup", path: "/integrations" },
                { label: "Webhooks", path: "/webhook" },
                { label: t("Header.trigger"), path: "/trigger" },
                { label: t("Header.bonus"), path: "/bonus" },
                { label: "Events", path: "/integration-events" },
              ].map(({ label, path }) => (
                <SubItem key={path} active={location.pathname.startsWith(path)} highlighted={highlightPath === path} onClick={() => navigate(path)}>
                  <SubItemDot active={location.pathname.startsWith(path) || highlightPath === path} />
                  <SubLabel>{label}</SubLabel>
                </SubItem>
              ))}
            </Collapse>
          )}

          {tip("Players",
            <NavItem
              data-onboarding="players-section"
              active={playerPaths.some(p => location.pathname.startsWith(p))}
              collapsed={collapsed}
              highlighted={highlightPath === "players-section"}
              onClick={() => { if (!collapsed) setPlayersOpen((p) => !p); else navigate("/users"); }}
            >
              <Users />
              {!collapsed && (
                <>
                  <ItemLabel>Players</ItemLabel>
                  {playersOpen
                    ? <ChevronUp style={{ width: 16, height: 16, opacity: 0.5 }} />
                    : <ChevronDown style={{ width: 16, height: 16, opacity: 0.5 }} />
                  }
                </>
              )}
            </NavItem>
          )}

          {!collapsed && (
            <Collapse in={playersOpen} timeout="auto">
              {[
                { label: "Users", path: "/users" },
                { label: "Segments", path: "/cohortes" },
                { label: "Currencies", path: "/currencies" },
              ].map(({ label, path }) => (
                <SubItem key={path} active={location.pathname.startsWith(path)} onClick={() => navigate(path)}>
                  <SubItemDot active={location.pathname.startsWith(path)} />
                  <SubLabel>{label}</SubLabel>
                </SubItem>
              ))}
            </Collapse>
          )}

          {navItems.map((item) =>
            tip(item.label,
              <NavItem
                key={item.key}
                data-onboarding={`nav-${item.key}`}
                active={location.pathname.startsWith(item.path)}
                collapsed={collapsed}
                highlighted={highlightPath === item.path}
                onClick={item.onClick}
              >
                {item.icon}
                {!collapsed && <ItemLabel>{item.label}</ItemLabel>}
              </NavItem>
            )
          )}

          {tip("Journeys in VOKO",
            <JourneysLink href={VOKO_URL} target="_blank" rel="noopener noreferrer" collapsed={collapsed}>
              <Route />
              {!collapsed && <ItemLabel>Journeys</ItemLabel>}
              {!collapsed && <NewBadge>New</NewBadge>}
            </JourneysLink>
          )}
        </NavList>

        <BottomArea>
          {tip("Monthly active users",
            <MauBadge
              collapsed={collapsed}
              onClick={() => navigate(`/events?fromDate=${format(startDate, "yyyy-MM-dd'T'00:00:00")}&toDate=${format(currentDate, "yyyy-MM-dd'T'23:59:59")}`)}
            >
              <Users style={{ width: 16, height: 16, color: "var(--g-accent)", flexShrink: 0 }} />
              {!collapsed && (
                <Typography fontSize={11} fontWeight={700} color="var(--g-accent)">
                  {monthlyActiveUsersCount}
                </Typography>
              )}
            </MauBadge>
          )}

          {onRestartGuide && tip("Setup Guide",
            <NavItem
              collapsed={collapsed}
              onClick={onRestartGuide}
            >
              <CircleHelp />
              {!collapsed && <ItemLabel>Guide</ItemLabel>}
            </NavItem>
          )}

          <UserRow collapsed={collapsed}>
            <Tooltip title={collapsed ? (profile?.email ?? "") : ""} placement="right">
              <Avatar
                sx={{
                  width: 32, height: 32, fontSize: 13, fontWeight: 700,
                  background: "var(--g-gradient-avatar)", color: "#fff", flexShrink: 0,
                  cursor: "pointer",
                }}
                onClick={() => !collapsed && setLogoutOpen(true)}
              >
                {(profile?.email || "U").charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
            {!collapsed && (
              <Box sx={{ overflow: "hidden", flex: 1, ml: 1 }}>
                <Typography fontSize={13} fontWeight={600} color="var(--g-text)" noWrap>
                  {profile?.email?.split("@")[0] || "User"}
                </Typography>
                <Typography fontSize={11.5} color="var(--g-text-3)" noWrap>
                  Admin
                </Typography>
              </Box>
            )}
            {!collapsed && (
              <Tooltip title={t("Header.logout")} placement="top">
                <Box
                  onClick={() => setLogoutOpen(true)}
                  sx={{ cursor: "pointer", display: "flex", alignItems: "center", color: "var(--g-text-2)", opacity: 0.7, "&:hover": { opacity: 1 } }}
                >
                  <LogOut style={{ width: 18, height: 18 }} />
                </Box>
              </Tooltip>
            )}
          </UserRow>
        </BottomArea>
      </SidebarRoot>

      <Modal open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <NavModalLogout onClose={() => setLogoutOpen(false)} />
      </Modal>
    </>
  );
};
